<template>
  <div class="content">
    <div class="col-md-8 ml-auto mr-auto">
      <h2 class="text-center">{{ $t('ptMappings.title') }}</h2>
      <p class="text-center">
        {{ $t('ptMappings.description') }}
      </p>
    </div>
    <div class="row mt-5">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <edit-pt-mapping-dialog :show="showEdit" :form-details="resourceToEdit" @close="showEdit=false"
                                  :operators="operators"/>
          <h4 slot="header" class="card-title">{{ $t('ptMappings.tableTitle') }}</h4>
          <div>
            <div
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <el-select
                class="select-primary mb-3 pagination-select"
                v-model="pagination.perPage"
                placeholder="Per page"
              >
                <el-option
                  class="select-primary"
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                >
                </el-option>
              </el-select>

              <div class="row">
                <div class="col-6">
                  <base-input>
                    <el-input
                      type="search"
                      class="mb-3 search-input"
                      prefix-icon="el-icon-search"
                      :placeholder="$t('generic.searchInTablePlaceholder')"
                      v-model="searchQuery"
                      aria-controls="datatables"
                    >
                    </el-input>
                  </base-input>
                </div>
                <div class="col-6" v-if="$auth.can({pt_mapping: 'create'}, 'permissions')">
                  <create-pt-mapping-btn :creation-disabled="operatorsLoading" :operators="operators"/>
                </div>
              </div>
            </div>

            <el-table v-loading="loading || operatorsLoading" :v-if="!loading && data" :data="queriedData"
                      :empty-text="$t('generic.noData')">
              <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :prop="column.prop"
                :label="$t(column.label)"
                :min-width="column.minWidth"
                :formatter="column.formatter"
              >
              </el-table-column>
              <!-- Actions -->
              <el-table-column
                v-if="$auth.can({pt_mapping: 'update'}, 'permissions') || $auth.can({pt_mapping: 'delete'}, 'permissions')"
                :min-width="50" align="right">
                <div slot-scope="props">
                  <base-button
                    v-if="$auth.can({pt_mapping: 'update'}, 'permissions')"
                    @click.native="handleEdit(props.$index, props.row)"
                    class="mr-2"
                    type="info"
                    size="sm"
                    icon
                    round
                    simple>
                    <i class="tim-icons icon-pencil"></i>
                  </base-button>
                  <!-- delete -->
                  <base-button
                    v-if="$auth.can({pt_mapping: 'delete'}, 'permissions')"
                    @click.native="handleDelete(props.$index, props.row)"
                    class=""
                    type="danger"
                    size="sm"
                    icon
                    round
                    simple>
                    <i class="tim-icons icon-simple-remove"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class="">
              <p class="card-category">
                {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>
  </div>
</template>
<script>
import {Option, Select, Table, TableColumn} from 'element-ui';
import {BaseAlert, BasePagination} from 'src/components';
import Fuse from 'fuse.js';
import swal from 'sweetalert2';
import {mapActions, mapState} from "vuex";
import CreatePtMappingBtn from './CreatePtMappingBtn';
import EditPtMappingDialog from './EditPtMappingDialog';
import operators from "@/pages/Operators/Operators.vue";

export default {
  name: 'pt-mappings',
  components: {
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    CreatePtMappingBtn,
    EditPtMappingDialog
  },
  computed: {
    operators() {
      return operators
    },
    ...mapState({
      loading: state => state.pt_mapping.loading,
      data: state => state.pt_mapping.data,
      operators: state => state.operator.data,
      operatorsLoading: state => state.operator.loading,
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableData.length;
    }
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['operator_id', 'garage_number', 'mapping_id'],
      tableColumns: [
        {
          prop: 'id',
          label: 'generic.id',
          minWidth: 120
        },
        {
          prop: 'operator_id',
          label: 'ptMappings.details.operator_id',
          // minWidth: 200
        },
        {
          prop: 'vehicle_type',
          label: 'ptMappings.details.vehicle_type',
          // minWidth: 200
        },
        {
          prop: 'garage_number',
          label: 'ptMappings.details.garage_number',
          // minWidth: 200
        },
        {
          prop: 'mapping_id',
          label: 'ptMappings.details.mapping_id',
          // minWidth: 200
        },
        {
          prop: 'updated_at',
          label: 'generic.updated',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
        },
        {
          prop: 'created_at',
          label: 'generic.created',
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
        },
      ],
      tableData: [],
      searchedData: [],
      fuseSearch: null,
      showEdit: false,
      resourceToEdit: {}
    };
  },
  methods: {
    ...mapActions(['LoadPTMappings', 'DeletePTMapping', 'LoadOperators']),
    handleEdit(index, row) {
      this.resourceToEdit = {
        id: row.id,
        operator_id: row.operator_id,
        garage_number: row.garage_number,
        mapping_id: row.mapping_id,
        vehicle_type: row.vehicle_type
      };
      this.showEdit = true;
    },
    handleDelete(index, row) {
      swal({
        title: this.$t('ptMappings.delete.text'),
        text: `${row.operator_id}: '${row.garage_number}' -> '${row.mapping_id}'`,
        confirmButtonText: this.$t('ptMappings.delete.actionBtnLabel'),
        cancelButtonText: this.$t('ptMappings.delete.cancelBtnLabel'),
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        type: 'question',
        showCancelButton: true
      }).then(result => {
        if (result.value) {
          this.DeletePTMapping(row).then(() => {
            if (this.error == null) {
              this.$notify({
                type: 'success',
                message: this.$t('ptMappings.delete.deletedNotification', {
                  operator_id: row.operator_id,
                  garage_number: row.garage_number,
                  mapping_id: row.mapping_id,
                  vehicle_type: row.vehicle_type
                }),
                icon: 'tim-icons icon-alert-circle-exc'
              })
            }
          })
        }
      });
    },
  },
  mounted() {
    this.LoadPTMappings()
    this.LoadOperators()
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
    "data"(mappings) {
      this.tableData = mappings
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: this.propsToSearch,
        threshold: 0.3
      });

      if (this.searchQuery != '') {
        this.searchedData = this.fuseSearch.search(this.searchQuery);
      }
    }
  }
};
</script>
<style scoped>
</style>
